/**
 * Générateur — Automatisme : savoir calculer l'aire des figures planes
 * usuelles : triangle, rectangle, disque.
 * Attendu ID : 1118 | Niveau : 4ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2026, Espace 4e) — rituel court.
 * Labels (cartes exerciseur) :
 *   - La bonne formule               (QCM ; relier figure cotée ↔ calcul ; V/F de pur rappel)
 *   - Calculer l'aire                (saisie sur figure cotée ; QCM à leurres de fausses formules ; V/F sur des calculs)
 *   - Retrouver une dimension        (saisie : largeur, côté, hauteur ou rayon connaissant l'aire)
 *
 * ── Plan de cartes validé (2026-09-09) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Représentation de l'espace 4e, automatisme
 * « Savoir calculer l'aire des figures planes usuelles : triangle, rectangle,
 * disque » + grille P1-P12. P1 : CM2 introduit (aire par quadrillage, cm²,
 * m²), 6e formalisé (aire du carré et du rectangle), 5e étendu (triangle,
 * disque, parallélogramme), 4e automatisme. P2 : sens installés — carreaux
 * unités, L × l, c × c, base × hauteur ÷ 2, π × r × r ; non installés
 * (distracteurs) — la hauteur qui n'est pas un côté, le périmètre, le
 * diamètre pris pour le rayon. P3 : longueurs entières ≤ 12, produits des
 * tables, triangle à base × hauteur pair, disque de rayon ≤ 6 en « k π ».
 * P5 : figures cotées, hauteur tracée et codée, rayon ou diamètre tracé.
 * P6 : aucune calculatrice, π symbolique. P7 : pas de valeur approchée, pas
 * de conversion. P8 : aire, base, hauteur, rayon, diamètre, cm², π. P9 :
 * tables, carrés de 0 à 12, la moitié. P10 : constat. P11 : côté oblique
 * pris pour la hauteur ; ÷ 2 oublié ; périmètre ; diamètre pour rayon ; cm
 * pour cm². P12 : rémanence forte.
 * INVARIANTS :
 *   - toute figure est CONSTRUITE depuis ses dimensions (cotes = paramètres,
 *     côté oblique du triangle cohérent : triplets 3-4-5, 6-8-10, 5-12-13) et
 *     porte data-figure / data-dims pour les solveurs ;
 *   - aires en ENTIERS (coefficient entier devant π) ; leurres calculés,
 *     distincts, positifs ; V/F : pas de crochets, mixité 1-3 faux, familles
 *     exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie, makeRelier,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1118;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FORMULE = 'La bonne formule';
const L_CALCUL = 'Calculer l\'aire';
const L_DIMENSION = 'Retrouver une dimension';

const UNITES = ['cm', 'cm', 'cm', 'dm', 'm'];
const FIGURES = ['rectangle', 'carre', 'triangle', 'disque'];
const NOM_FIGURE = { rectangle: 'un rectangle de longueur L et de largeur l', carre: 'un carré de côté c', triangle: 'un triangle de base b et de hauteur h', disque: 'un disque de rayon r' };
const NOM_COURT = { rectangle: 'un rectangle', carre: 'un carré', triangle: 'un triangle', disque: 'un disque' };
const FORMULES = {
    rectangle: { juste: 'A = L × l', fausses: ['A = 2 × (L + l)', 'A = L + l', 'A = L × l ÷ 2'] },
    carre: { juste: 'A = c × c', fausses: ['A = 4 × c', 'A = c × 2', 'A = c + c + c + c'] },
    triangle: { juste: 'A = base × hauteur ÷ 2', fausses: ['A = base × hauteur', 'A = base + hauteur', 'A = (base + hauteur) ÷ 2'] },
    disque: { juste: 'A = π × r × r', fausses: ['A = 2 × π × r', 'A = π × r', 'A = π × r × 2'] },
};

// ─── Tirage des figures (aires entières, produits des tables) ──────────────
// Triplets pour le côté oblique du triangle quelconque : (hauteur, décalage horizontal, oblique)
const TRIPLETS = [[3, 4, 5], [4, 3, 5], [6, 8, 10], [8, 6, 10], [5, 12, 13], [12, 5, 13]];
const tirerFigure = (type = randomChoice(FIGURES)) => {
    const u = randomChoice(UNITES);
    if (type === 'rectangle') {
        // longueur ≤ 10 : produits des tables (revue 2026-09-09) ; le carré garde 11 et 12 (carrés parfaits, automatisme 4e)
        for (let essai = 0; essai < 100; essai++) { const L = randInt(3, 10); const l = randInt(2, 9); if (L > l) return { type, u, L, l, A: L * l }; }
        return { type, u, L: 7, l: 4, A: 28 };
    }
    if (type === 'carre') { const c = randInt(2, 12); return { type, u, c, A: c * c }; }
    if (type === 'triangle') {
        if (Math.random() < 0.5) {
            // triangle rectangle : les deux côtés de l'angle droit sont base et hauteur, l'hypoténuse est cotée (leurre)
            const [h, x, o] = randomChoice(TRIPLETS); return { type, u, forme: 'rectangle', b: x, h, oblique: o, A: (x * h) / 2 };
        }
        for (let essai = 0; essai < 100; essai++) {
            const [h, x, o] = randomChoice(TRIPLETS); const b = randInt(x + 1, 12);
            if ((b * h) % 2 !== 0 || b > 12) continue;
            return { type, u, forme: 'quelconque', b, h, x, oblique: o, A: (b * h) / 2 };
        }
        return { type, u, forme: 'quelconque', b: 8, h: 3, x: 4, oblique: 5, A: 12 };
    }
    const r = randInt(1, 6); const parDiametre = Math.random() < 0.35;
    return { type, u, r, parDiametre, d: 2 * r, A: r * r };                                       // A = k π
};
const aireAffichee = (F) => (F.type === 'disque' ? `${F.A}π ${F.u}²` : `${F.A} ${F.u}²`);

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Valeurs fausses nommées d'une aire (le nombre devant π pour le disque), celles des leurres du QCM qui sont une vraie erreur.
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : le carré et le disque ont leurs propres codes, ici, dans
// CODES_FORMULE et dans POOL_RAPPEL (les leurres du relier gardent leurs codes).
const erreursAire = (F) => {
    if (F.type === 'rectangle') return [['perimetre_pour_aire', 2 * (F.L + F.l)], ['addition_pour_produit', F.L + F.l]];
    if (F.type === 'carre') return [['perimetre_pour_aire', 4 * F.c], ['carre_cote_fois_2', 2 * F.c]];
    if (F.type === 'triangle') {
        return [['triangle_sans_le_demi', F.b * F.h], ['cote_pris_pour_hauteur', (F.b * F.oblique) / 2], ['addition_pour_produit', F.b + F.h],
            ...(F.forme === 'rectangle' ? [['perimetre_pour_aire', F.b + F.h + F.oblique]] : [])];
    }
    return [['perimetre_du_disque_pour_aire', 2 * F.r], ['exposant_ignore', F.r], ...(F.parDiametre ? [['diametre_pour_rayon', 4 * F.r * F.r]] : [])];
};
/** Les erreurs sûres : valeurs entières positives, ni la bonne réponse, ni portées par deux codes. */
const erreursSures = (paires, juste) => {
    const parValeur = [];
    paires.forEach(([code, v]) => {
        if (!Number.isInteger(v) || v <= 0 || v === juste) return;
        const deja = parValeur.find((p) => p.v === v);
        if (deja) deja.codes.add(code); else parValeur.push({ v, codes: new Set([code]) });
    });
    return parValeur.filter((p) => p.codes.size === 1).map((p) => ({ code: [...p.codes][0], v: p.v }));
};
// Formules fausses nommées (la bonne formule d'une figure demandée) ; « A = L × l ÷ 2 » reste sans code.
const CODES_FORMULE = {
    'A = 2 × (L + l)': 'perimetre_pour_aire',
    'A = L + l': 'addition_pour_produit',
    'A = 4 × c': 'perimetre_pour_aire',
    'A = c × 2': 'carre_cote_fois_2',
    'A = c + c + c + c': 'perimetre_pour_aire',
    'A = base × hauteur': 'triangle_sans_le_demi',
    'A = base + hauteur': 'addition_pour_produit',
    'A = (base + hauteur) ÷ 2': 'addition_pour_produit',
    'A = 2 × π × r': 'perimetre_du_disque_pour_aire',
    'A = π × r': 'exposant_ignore',
    'A = π × r × 2': 'perimetre_du_disque_pour_aire',
};

// ─── Figures cotées (SVG construites) ──────────────────────────────────────
const VB = { w: 240, h: 170 };
const COTE = (x, y, txt, anchor = 'middle') => `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" font-family="sans-serif" font-size="12" text-anchor="${anchor}" fill="#b45309">${txt}</text>`;
const dataAttrs = (F) => {
    const dims = F.type === 'rectangle' ? `L=${F.L};l=${F.l}` : F.type === 'carre' ? `c=${F.c}` : F.type === 'triangle' ? `b=${F.b};h=${F.h};o=${F.oblique}` : (F.parDiametre ? `d=${F.d}` : `r=${F.r}`);
    return `data-figure="${F.type}" data-unite="${F.u}" data-dims="${dims}"`;
};
const svgWrap = (F, inner) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB.w} ${VB.h}" width="${VB.w}" height="${VB.h}" ${dataAttrs(F)} data-v="${dataAttrs(F).length + (F.A || 0)}">${inner}</svg>`;
const TRAIT = 'stroke="#1f2937" stroke-width="1.8" stroke-linejoin="round"';

const figure = (F) => {
    if (F.type === 'rectangle' || F.type === 'carre') {
        const L = F.type === 'carre' ? F.c : F.L; const l = F.type === 'carre' ? F.c : F.l;
        // échelle bornée : la figure occupe toujours une bonne part du cadre (un carré de 3 n'est pas minuscule ; revue 2026-09-09)
        const s = Math.min(36, 170 / L, 110 / l); const w = L * s; const h = l * s; const x0 = (VB.w - w) / 2 - 10; const y0 = (VB.h - h) / 2 - 8;
        let g = `<rect x="${x0.toFixed(1)}" y="${y0.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" fill="#EFF6FF" ${TRAIT}/>`;
        g += COTE(x0 + w / 2, y0 + h + 16, `${L} ${F.u}`);
        if (F.type === 'rectangle') g += COTE(x0 + w + 8, y0 + h / 2 + 4, `${l} ${F.u}`, 'start');
        return svgWrap(F, g);
    }
    if (F.type === 'triangle') {
        const s = Math.min(30, 180 / F.b, 110 / F.h); const b = F.b * s; const h = F.h * s;
        const x0 = (VB.w - b) / 2; const yb = (VB.h + h) / 2 - 4; const xa = F.forme === 'rectangle' ? x0 : x0 + (F.b - F.x) * s;   // pied de la hauteur
        const A = [x0, yb]; const B = [x0 + b, yb]; const S = [xa, yb - h];
        let g = `<polygon points="${A[0].toFixed(1)},${A[1]} ${B[0].toFixed(1)},${B[1]} ${S[0].toFixed(1)},${S[1].toFixed(1)}" fill="#EFF6FF" ${TRAIT}/>`;
        // hauteur : en pointillés si elle est intérieure, confondue avec un côté si le triangle est rectangle
        if (F.forme === 'quelconque') g += `<line x1="${xa.toFixed(1)}" y1="${yb}" x2="${xa.toFixed(1)}" y2="${(yb - h).toFixed(1)}" stroke="#1f2937" stroke-width="1.4" stroke-dasharray="5 4"/>`;
        const q = 9; g += `<path d="M ${(xa + q).toFixed(1)} ${yb} L ${(xa + q).toFixed(1)} ${yb - q} L ${xa.toFixed(1)} ${yb - q}" fill="none" stroke="#1f2937" stroke-width="1.2"/>`;
        g += COTE(x0 + b / 2, yb + 16, `${F.b} ${F.u}`);
        // cote de la hauteur du côté où il y a le plus de place (à gauche du pointillé en général), loin de la cote du côté oblique
        const aGauche = F.forme === 'rectangle' || (F.b - F.x) >= F.x;
        g += COTE(xa + (aGauche ? -6 : 6), yb - h / 2 + 4, `${F.h} ${F.u}`, aGauche ? 'end' : 'start');
        // côté oblique coté (le leurre « côté pris pour hauteur ») : de S vers B
        g += COTE((S[0] + B[0]) / 2 + 10, (S[1] + B[1]) / 2 - 4, `${F.oblique} ${F.u}`, 'start');
        return svgWrap(F, g);
    }
    const R = Math.min(60, 40 + F.r * 4); const cx = VB.w / 2 - 20; const cy = VB.h / 2;
    let g = `<circle cx="${cx}" cy="${cy}" r="${R}" fill="#EFF6FF" ${TRAIT}/><circle cx="${cx}" cy="${cy}" r="2.2" fill="#1f2937"/>`;
    if (F.parDiametre) { g += `<line x1="${cx - R}" y1="${cy}" x2="${cx + R}" y2="${cy}" stroke="#b45309" stroke-width="1.6"/>`; g += COTE(cx, cy - 8, `${F.d} ${F.u}`); }
    else { g += `<line x1="${cx}" y1="${cy}" x2="${cx + R}" y2="${cy}" stroke="#b45309" stroke-width="1.6"/>`; g += COTE(cx + R / 2, cy - 8, `${F.r} ${F.u}`); }
    return svgWrap(F, g);
};

const enonceFigure = (F) => {
    if (F.type === 'rectangle') return `<p>Voici un rectangle de longueur ${F.L} ${F.u} et de largeur ${F.l} ${F.u}.</p>`;
    if (F.type === 'carre') return `<p>Voici un carré de côté ${F.c} ${F.u}.</p>`;
    if (F.type === 'triangle') return `<p>Voici un triangle de base ${F.b} ${F.u}, dont la hauteur correspondante mesure ${F.h} ${F.u}.</p>`;
    return F.parDiametre ? `<p>Voici un disque de diamètre ${F.d} ${F.u}.</p>` : `<p>Voici un disque de rayon ${F.r} ${F.u}.</p>`;
};
const signatureDe = (F) => `${F.type}-${F.u}-${dataAttrs(F).match(/data-dims="([^"]+)"/)[1]}`;

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refF = { v: null }; const refR = { v: null }; const refS = { v: null }; const refQ = { v: null }; const refD = { v: null };

// ─── Carte 1a : la bonne formule (QCM, trois questions) ────────────────────

const construireFormuleQcm = () => {
    const cas = randomChoice(['formule', 'figure', 'intrus']);
    // « quelle figure ? » : formules propres à UNE figure (c × c, base × hauteur ÷ 2, π r r) — L × l vaut aussi pour le carré
    const type = cas === 'figure' ? randomChoice(['carre', 'triangle', 'disque']) : randomChoice(FIGURES); const F = FORMULES[type];
    let payload;
    if (cas === 'formule') payload = makeQcm(shuffle([F.juste, ...F.fausses]), F.juste, `<p>Quelle formule donne l'aire d'${NOM_FIGURE[type]} ?</p>`, opts(L_FORMULE));
    else if (cas === 'figure') payload = makeQcm(shuffle(FIGURES.map((t) => NOM_COURT[t])), NOM_COURT[type], `<p>Quelle figure a pour aire ${F.juste} ?</p>`, opts(L_FORMULE));
    else {
        // intrus : jamais une formule vraie pour une autre figure (base × hauteur = parallélogramme, L × l ÷ 2 = triangle rectangle ; revue 2026-09-09)
        const INTRUS_EXCLUS = ['A = base × hauteur', 'A = L × l ÷ 2'];
        const fausse = randomChoice(F.fausses.filter((f) => !INTRUS_EXCLUS.includes(f))); const autres = shuffle(FIGURES.filter((t) => t !== type)).slice(0, 2).map((t) => FORMULES[t].juste);
        payload = makeQcm(shuffle([fausse, F.juste, ...autres]), fausse, `<p>Une seule de ces formules d'aire est fausse. Laquelle ?</p>`, opts(L_FORMULE));
        payload.contenu = { ...payload.contenu, negated: true, sousType: cas };
        payload._meta.params = { cas, type, fausse, signature: `${cas}-${type}-${fausse}` };
        return payload;
    }
    payload.contenu = { ...payload.contenu, sousType: cas };
    payload._meta.params = { cas, type, signature: `${cas}-${type}` };
    // Seule la question « quelle formule donne l'aire ? » nomme ses choix faux.
    if (cas === 'formule') {
        const erreurs = F.fausses.filter((f) => CODES_FORMULE[f]).map((f) => ({ code: CODES_FORMULE[f], valeur: f }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    }
    return payload;
};
const genFormuleQcm = () => antiRepeat(construireFormuleQcm, refF);

// ─── Carte 1b : relier figure cotée ↔ calcul de son aire ───────────────────

const calculDe = (F) => (F.type === 'rectangle' ? `${F.L} × ${F.l}` : F.type === 'carre' ? `${F.c} × ${F.c}` : F.type === 'triangle' ? `${F.b} × ${F.h} ÷ 2` : `π × ${F.r} × ${F.r}`);
const sourceDe = (F) => (F.type === 'rectangle' ? `Rectangle de ${F.L} ${F.u} sur ${F.l} ${F.u}` : F.type === 'carre' ? `Carré de côté ${F.c} ${F.u}` : F.type === 'triangle' ? `Triangle de base ${F.b} ${F.u} et de hauteur ${F.h} ${F.u}` : `Disque de rayon ${F.r} ${F.u}`);
const leurreCalcul = (F) => (F.type === 'rectangle' ? [`2 × (${F.L} + ${F.l})`, `${F.L} + ${F.l}`] : F.type === 'carre' ? [`4 × ${F.c}`, `${F.c} × 2`] : F.type === 'triangle' ? [`${F.b} × ${F.h}`, `${F.b} + ${F.h}`] : [`2 × π × ${F.r}`, `π × ${F.r}`]);
const valeurCalcul = (e) => `${e.includes('π') ? 'π' : ''}${Function(`return (${e.replace(/π ×/g, '').replace(/×/g, '*').replace(/÷/g, '/')})`)()}`;
// L'erreur que chaque leurre de leurreCalcul incarne, dans le même ordre (π × r : le carré du rayon ignoré).
const codesLeurres = (F) => (F.type === 'rectangle' ? ['perimetre_pour_aire', 'addition_pour_produit'] : F.type === 'carre' ? ['perimetre_pour_aire', 'base_fois_exposant']
    : F.type === 'triangle' ? ['triangle_sans_le_demi', 'addition_pour_produit'] : ['perimetre_pour_aire', 'exposant_ignore']);

const construireRelier = () => {
    for (let essai = 0; essai < 60; essai++) {
        const u = randomChoice(UNITES); const types = shuffle(FIGURES).slice(0, 3);
        const figs = types.map((t) => ({ ...tirerFigure(t), u, parDiametre: false }));
        const pairs = figs.map((F) => ({ source: sourceDe(F), target: calculDe(F) }));
        const cibles = pairs.map((q) => q.target);
        const leurres = shuffle(figs.flatMap((F) => leurreCalcul(F).map((t, k) => ({ target: t, code: codesLeurres(F)[k] })))).filter((l) => !cibles.includes(l.target)).slice(0, 2);
        const toutes = [...cibles, ...leurres.map((l) => l.target)];
        if (new Set(toutes).size !== 5 || new Set(toutes.map(valeurCalcul)).size !== 5) continue;      // ni doublon, ni coïncidence numérique
        const payload = makeRelier(pairs, '<p>Relie chaque figure au calcul de son aire.</p>', { ...opts(L_FORMULE), cibles_leurres: leurres });
        payload._meta.params = { types, figs, signature: cibles.slice().sort().join('|') };
        // Une cible que deux erreurs différentes produisent (carré de côté 3 et triangle 4 × 3 : « 4 × 3 ») reste sans code.
        const codesDe = (t) => new Set(figs.flatMap((F) => leurreCalcul(F).flatMap((x, k) => (x === t ? [codesLeurres(F)[k]] : []))));
        payload.contenu = { ...payload.contenu, cibles_leurres: payload.contenu.cibles_leurres.map((l) => (codesDe(l.target).size > 1 ? { target: l.target } : l)) };
        return payload;
    }
    throw new Error('gen_1118 : relier introuvable');
};
const genRelier = () => antiRepeat(construireRelier, refR);

// ─── Carte 1c : formules, vrai ou faux ? (pur rappel) ──────────────────────

const POOL_RAPPEL = [
    { l: 'L\'aire d\'un rectangle, c\'est longueur × largeur.', v: true, f: 'rectangle' },
    { l: 'L\'aire d\'un rectangle, c\'est 2 × (longueur + largeur).', v: false, f: 'rectangle', code: 'perimetre_pour_aire' },
    { l: 'L\'aire d\'un carré de côté 5 cm est 5 × 5 cm², soit 25 cm².', v: true, f: 'carre' },
    { l: 'L\'aire d\'un carré de côté 5 cm est 4 × 5 cm², soit 20 cm².', v: false, f: 'carre', code: 'perimetre_pour_aire' },
    { l: 'L\'aire d\'un triangle, c\'est base × hauteur ÷ 2.', v: true, f: 'triangle' },
    { l: 'L\'aire d\'un triangle, c\'est base × hauteur.', v: false, f: 'triangle', code: 'triangle_sans_le_demi' },
    { l: 'Dans un triangle, la hauteur est perpendiculaire à la base.', v: true, f: 'hauteur' },
    { l: 'Dans un triangle, n\'importe quel côté peut servir de hauteur.', v: false, f: 'hauteur', code: 'cote_pris_pour_hauteur' },
    { l: 'L\'aire d\'un disque, c\'est π × rayon × rayon.', v: true, f: 'disque' },
    { l: 'L\'aire d\'un disque, c\'est 2 × π × rayon.', v: false, f: 'disque', code: 'perimetre_du_disque_pour_aire' },
    { l: 'Si on connaît le diamètre d\'un disque, on le divise par 2 pour avoir le rayon avant de calculer l\'aire.', v: true, f: 'diametre' },
    { l: 'L\'aire d\'un disque, c\'est π × diamètre × diamètre.', v: false, f: 'diametre', code: 'diametre_pour_rayon' },
    { l: 'Une aire s\'exprime en cm², en dm² ou en m².', v: true, f: 'unite' },
    { l: 'Une aire s\'exprime en cm.', v: false, f: 'unite', code: 'unite_confondue' },
    // « périmètre, pas son aire » rejoint la famille rectangle : elle donnait la réponse de la fausse du rectangle (revue 2026-09-09)
    { l: '2 × (longueur + largeur), c\'est le périmètre du rectangle, pas son aire.', v: true, f: 'rectangle' },
    { l: 'Deux figures de même aire peuvent avoir des périmètres différents.', v: true, f: 'perimetre' },
    { l: 'Le périmètre et l\'aire d\'une figure sont toujours égaux.', v: false, f: 'perimetre', code: 'perimetre_pour_aire' },
];

let dernieresLignesRappel1118 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1118 = [];
        if (lignes.some((it) => dernieresLignesRappel1118.includes(it.l))) continue;
        dernieresLignesRappel1118 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les aires, dis si elle est vraie ou fausse.</p>',
            opts(L_FORMULE),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1118 : V/F rappel sans tirage après 160 essais');
};

// ─── Carte 2a : calculer l'aire (saisie sur figure) ────────────────────────

const construireCalculSaisie = () => {
    const F = tirerFigure();
    const question = F.type === 'disque' ? `<p>Quelle est son aire, en ${F.u}² ? Donne le nombre devant π.</p>` : `<p>Quelle est son aire, en ${F.u}² ?</p>`;
    const payload = makeSaisie(String(F.A), enonceFigure(F) + question, { ...opts(L_CALCUL), suffixe: F.type === 'disque' ? `π ${F.u}²` : `${F.u}²`, elements_additionnels: imgElement(figure(F), 'medium') });
    payload.contenu = { ...payload.contenu, sousType: F.type };
    payload._meta.params = { F, signature: signatureDe(F) };
    const erreurs = erreursSures(erreursAire(F), F.A).map((e) => ({ code: e.code, valeur: String(e.v) }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genCalculSaisie = () => antiRepeat(construireCalculSaisie, refS);

// ─── Carte 2b : calculer l'aire (QCM à leurres de fausses formules) ────────

const leurresDe = (F) => {
    // secours : leurres faibles (aucune erreur naturelle), pris seulement s'il manque un troisième leurre (revue 2026-09-09)
    // rang de la bonne réponse : un leurre plausible de chaque côté de l'aire (L × L, c × c ÷ 2, π r r ÷ 2), sinon l'aire
    // n'est jamais la plus petite ni la plus grande option (revue 2026-09-09)
    if (F.type === 'rectangle') return [{ v: 2 * (F.L + F.l), role: 'perimetre' }, { v: F.L + F.l, role: 'somme' }, { v: F.L * F.L, role: 'longueur_carree' }, { v: 2 * F.A, role: 'double' }, { v: F.A / 2, role: 'moitie', secours: true }];
    if (F.type === 'carre') return [{ v: 4 * F.c, role: 'perimetre' }, { v: 2 * F.c, role: 'double_cote' }, { v: F.c * F.c * 2, role: 'double' }, { v: F.c * F.c / 2, role: 'moitie' }, { v: F.c * 4 + F.c, role: 'cinq_fois', secours: true }];
    if (F.type === 'triangle') return [{ v: F.b * F.h, role: 'sans_demi' }, { v: (F.b * F.oblique) / 2, role: 'cote_oblique' }, { v: F.b + F.h, role: 'somme' }, { v: F.b + F.h + F.oblique, role: 'perimetre' }, { v: F.A / 2, role: 'demi_deux_fois' }];
    // « diamètre au carré » seulement quand le diamètre est donné : avec le rayon, π × 10 × 10 pour r = 5 n'est pas une erreur naturelle (revue 2026-09-09)
    return [{ v: 2 * F.r, role: 'perimetre' }, { v: F.r, role: 'rayon' }, ...(F.parDiametre ? [{ v: 4 * F.r * F.r, role: 'diametre_carre' }] : []), { v: 2 * F.r * F.r, role: 'double' }, { v: F.r * F.r / 2, role: 'moitie' }];
};

const construireCalculQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const F = tirerFigure();
        const cands = [];
        const valide = (l) => Number.isInteger(l.v) && l.v > 0 && l.v !== F.A && !cands.some((m) => m.v === l.v);
        const pool = leurresDe(F); shuffle(pool.filter((l) => !l.secours)).forEach((l) => { if (valide(l)) cands.push(l); });
        // nombre de leurres SOUS la bonne réponse tiré entre 0 et 3 (équilibre des positions)
        const nBas = randInt(0, 3); const leurres = [];
        cands.filter((l) => l.v < F.A).forEach((l) => { if (leurres.length < nBas) leurres.push(l); });
        cands.filter((l) => l.v > F.A).forEach((l) => { if (leurres.length < 3) leurres.push(l); });
        cands.forEach((l) => { if (leurres.length < 3 && !leurres.includes(l)) leurres.push(l); });
        // secours : seulement s'il manque un troisième leurre
        pool.filter((l) => l.secours).forEach((l) => { if (leurres.length < 3 && valide(l)) { cands.push(l); leurres.push(l); } });
        if (leurres.length < 3) continue;
        const aff = (v) => (F.type === 'disque' ? `${v}π ${F.u}²` : `${v} ${F.u}²`);
        const options = shuffle([F.A, ...leurres.map((l) => l.v)].map(aff));
        const payload = makeQcm(options, aff(F.A), enonceFigure(F) + '<p>Quelle est son aire ?</p>', { ...opts(L_CALCUL), elements_additionnels: imgElement(figure(F), 'medium') });
        payload.contenu = { ...payload.contenu, sousType: F.type };
        payload._meta.params = { F, roles: leurres.map((l) => l.role), signature: signatureDe(F) };
        const erreurs = erreursSures(erreursAire(F), F.A).map((e) => ({ code: e.code, valeur: aff(e.v) })).filter((e) => options.includes(e.valeur));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1118 : QCM sans leurres');
};
const genCalculQcm = () => antiRepeat(construireCalculQcm, refQ);

// ─── Carte 3 : retrouver une dimension (division dans les tables) ──────────

const construireDimension = () => {
    const type = randomChoice(FIGURES); const u = randomChoice(UNITES); let texte; let reponse; let params;
    if (type === 'rectangle') { let L = randInt(3, 10); let l = randInt(2, 9); while (l >= L) { L = randInt(3, 10); l = randInt(2, 9); } texte = `<p>Un rectangle a pour aire ${L * l} ${u}². Sa longueur mesure ${L} ${u}.</p><p>Quelle est sa largeur, en ${u} ?</p>`; reponse = l; params = { L, l }; }
    else if (type === 'carre') { const c = randInt(2, 12); texte = `<p>Un carré a pour aire ${c * c} ${u}².</p><p>Quelle est la longueur de son côté, en ${u} ?</p>`; reponse = c; params = { c }; }
    else if (type === 'triangle') { let b = randInt(2, 10); let h = randInt(2, 10); while ((b * h) % 2 !== 0) { b = randInt(2, 10); h = randInt(2, 10); } texte = `<p>Un triangle a pour aire ${(b * h) / 2} ${u}². Sa base mesure ${b} ${u}.</p><p>Quelle est la hauteur correspondante, en ${u} ?</p>`; reponse = h; params = { b, h }; }
    else { const r = randInt(2, 6); texte = `<p>Un disque a pour aire ${r * r}π ${u}².</p><p>Quel est son rayon, en ${u} ?</p>`; reponse = r; params = { r }; }
    const payload = makeSaisie(String(reponse), texte, { ...opts(L_DIMENSION), suffixe: u });
    payload.contenu = { ...payload.contenu, sousType: type };
    payload._meta.params = { type, u, ...params, reponse, signature: `dim-${type}-${Object.values(params).join('x')}` };
    return payload;
};
const genDimension = () => antiRepeat(construireDimension, refD);

// ─── Carte 2c : vrai ou faux ? (calculs, rattaché à « Calculer l'aire », prof 2026-09-09) ─

let dernieresLignesVf1118 = [];

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const candidates = shuffle(FIGURES).map((type) => {
            const F = { ...tirerFigure(type), parDiametre: false };
            const faux = shuffle(leurresDe(F)).find((l) => Number.isInteger(l.v) && l.v > 0 && l.v !== F.A);
            const vrai = Math.random() < 0.5 || !faux; const val = vrai ? F.A : faux.v;
            const desc = F.type === 'rectangle' ? `L'aire d'un rectangle de longueur ${F.L} ${F.u} et de largeur ${F.l} ${F.u} est ${val} ${F.u}².`
                : F.type === 'carre' ? `L'aire d'un carré de côté ${F.c} ${F.u} est ${val} ${F.u}².`
                : F.type === 'triangle' ? `L'aire d'un triangle de base ${F.b} ${F.u} et de hauteur ${F.h} ${F.u} est ${val} ${F.u}².`
                : `L'aire d'un disque de rayon ${F.r} ${F.u} est ${val}π ${F.u}².`;
            // Code de la valeur fausse, si elle est une erreur nommée sans ambiguïté.
            const code = vrai ? null : (erreursSures(erreursAire(F), F.A).find((e) => e.v === val) || {}).code;
            return { l: desc, v: vrai, f: type, code };
        });
        const nbFaux = candidates.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        const lignes = shuffle(candidates);
        if (essai > 120) dernieresLignesVf1118 = [];
        if (lignes.some((it) => dernieresLignesVf1118.includes(it.l))) continue;
        dernieresLignesVf1118 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_CALCUL),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { lignes: lignes.map((it) => it.l) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1118 : V/F sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    formule_qcm_unique: genFormuleQcm,
    formule_relier: genRelier,
    formule_tableau_vf: genRappelVf,
    calcul_saisie: genCalculSaisie,
    calcul_qcm_unique: genCalculQcm,
    dimension_saisie: genDimension,
    calcul_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'formule', label: L_FORMULE, formatsList: buildFormats('formule') },
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
    { id: 'dimension', label: L_DIMENSION, formatsList: buildFormats('dimension') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    calcul_qcm_unique: ['cote_pris_pour_hauteur'],
    // relecture du 04/10/2026 : on ne garde un code que sur les cartes où le juge l'a lu ; et au vrai/faux de
    // calcul, sans figure, le côté oblique n'est montré nulle part (juge du 03/10) : cote_pris_pour_hauteur sort
    formule_tableau_vf: ['perimetre_du_disque_pour_aire'],
    calcul_tableau_vf: ['perimetre_du_disque_pour_aire', 'carre_cote_fois_2', 'cote_pris_pour_hauteur'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
